import * as React from 'react';
import { ClearButton } from '../../../../components/button/ClearButton';
import { EnteredDay } from '../../../../modules/ordering/models/orders-entered';
import { OrderingSvc } from '../../../../modules/ordering/service';
import { useTileData } from './use-tile-data';

const Weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The most of any day is a bar this high
const TallestBarInRem = 12;

const orders = (count: number) => `${ count.toLocaleString() } ${ count === 1 ? 'order' : 'orders' }`;

/**
 * How much more or less there is than before, in whole percent: undefined when there was nothing before to compare with
 */
export const change = (now: number, before: number) => {
    return before === 0 ? undefined : Math.round((now - before) / before * 100);
};

const written = (percent: number) => `${ percent > 0 ? '+' : '' }${ percent }%`;

// to the hundredth of a rem, so that no sum of fractions shows in the style of a bar
const barHeight = (count: number, most: number) => {
    return count <= 0 ? '0rem' : `${ Math.max(0.2, Math.round(count / most * TallestBarInRem * 100) / 100) }rem`;
};

/**
 * The orders entered on each of the last seven days, next to the same days of the week before.  An order is entered when it
 * reaches this system, and today is counted up to now.
 */
export const OrdersEnteredTile = () => {

    const [service] = React.useState(() => new OrderingSvc());
    const { data: entered, failed, reload } = useTileData('orders-entered', () => service.getOrdersEntered());

    const days = entered?.days ?? [];
    const total = days.reduce((sum, x) => sum + x.entered, 0);
    const before = days.reduce((sum, x) => sum + x.enteredWeekBefore, 0);
    const most = Math.max(1, ...days.flatMap(x => [x.entered, x.enteredWeekBefore]));
    const percent = change(total, before);

    const dayName = (day: EnteredDay, i: number) => i === days.length - 1 ? 'Today' : Weekdays[day.date.getDay()];

    const body = () => {
        if (failed) {
            return <div className="dashboard-tile-message">
                <p>Couldn't load the orders entered.</p>
                <ClearButton id="entered-retry" text="Try again" icon="" classes="action" onBtnClick={ reload } />
            </div>;
        }

        if (!entered) {
            return <p className="dashboard-tile-message">Loading…</p>;
        }

        if (total === 0 && before === 0) {
            return <p className="dashboard-tile-message">No orders were entered in the last 14 days.</p>;
        }

        return <React.Fragment>
            <ul className="entered-chart">
                {
                    days.map((day, i) => (
                        <li key={ day.date.getTime() } className="entered-day">
                            <span className="sr-only">
                                { `${ dayName(day, i) } ${ day.date.getDate() }: ${ orders(day.entered) } entered, ${ day.enteredWeekBefore.toLocaleString() } the week before` }
                            </span>
                            <div className="entered-bars" aria-hidden="true">
                                <div className="entered-column">
                                    <span className="entered-value">{ day.enteredWeekBefore.toLocaleString() }</span>
                                    <div className="entered-bar entered-bar-before" style={ { height: barHeight(day.enteredWeekBefore, most) } } />
                                </div>
                                <div className="entered-column">
                                    <span className="entered-value entered-value-now">{ day.entered.toLocaleString() }</span>
                                    <div className="entered-bar entered-bar-now" style={ { height: barHeight(day.entered, most) } } />
                                </div>
                            </div>
                            <span className="entered-label" aria-hidden="true">
                                { dayName(day, i) }
                                <span className="entered-date">{ day.date.getDate() }</span>
                            </span>
                        </li>
                    ))
                }
            </ul>
            <ul className="entered-legend">
                <li><span className="entered-swatch entered-swatch-now" />Last 7 days <strong>{ total.toLocaleString() }</strong></li>
                <li><span className="entered-swatch entered-swatch-before" />The week before <strong>{ before.toLocaleString() }</strong></li>
                <li>Change <strong>{ percent === undefined ? 'none to compare with' : written(percent) }</strong></li>
            </ul>
            <p className="dashboard-tile-note">Today is counted up to now.</p>
        </React.Fragment>;
    };

    return <section className="dashboard-tile orders-entered" aria-labelledby="orders-entered-heading">
        <div className="dashboard-tile-heading">
            <h2 id="orders-entered-heading">Orders entered</h2>
            {
                entered && (total > 0 || before > 0)
                    ? <span className="dashboard-tile-total">{ orders(total) } in the last 7 days</span>
                    : null
            }
        </div>
        <p className="dashboard-tile-scope">Orders that reached the system each day, next to the same day the week before</p>
        { body() }
    </section>;
};
